import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "../cx.ts";

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title?: ReactNode;
  /** Short line under the title. */
  description?: ReactNode;
  /** Buttons or links at the right of the header. */
  actions?: ReactNode;
  /** Remove the body padding (tables, lists that run edge to edge). */
  flush?: boolean;
  /** Heading level for the title; defaults to h2. */
  headingLevel?: 2 | 3;
}

/** A surface for one block of content. Renders a <section> labelled by its title. */
export function Card({
  title,
  description,
  actions,
  flush = false,
  headingLevel = 2,
  className,
  children,
  ...rest
}: CardProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <section
      className={cx(
        "rounded-xl border border-line bg-surface shadow-[0_1px_2px_rgb(15_28_46/0.04)]",
        "sun:rounded-md sun:border-2 sun:border-black sun:shadow-none",
        className,
      )}
      {...rest}
    >
      {title || actions ? (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sun:border-black">
          <div className="min-w-0">
            {title ? (
              <Heading className="font-semibold text-base text-ink leading-6">{title}</Heading>
            ) : null}
            {description ? <p className="mt-0.5 text-ink-muted text-sm">{description}</p> : null}
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </header>
      ) : null}
      <div className={flush ? undefined : "p-4"}>{children}</div>
    </section>
  );
}
